import React, { useState, useEffect, useRef, useCallback } from 'react';
import ePub from 'epubjs';
import { auth, db, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import {
  doc, getDoc, setDoc, addDoc, updateDoc, collection, query, where,
  orderBy, getDocs, deleteDoc, serverTimestamp
} from 'firebase/firestore';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Textarea } from './components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import {
  BookOpen, List, Bookmark, BookmarkCheck, X, Loader2, ArrowLeft,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Type, Sun, Moon, Palette,
  Highlighter, StickyNote, Trash2, Smartphone, Check, Info, HelpCircle, Wifi
} from 'lucide-react';
import {
  isNative, cacheEbookContent, getCachedEbookContent,
  saveProgressOffline, isOnline, onNetworkChange,
  getPendingProgress, clearPendingProgress
} from './lib/offlineStorage';

/* ═══════════════════════════════════════════════════
   EBOOK READER
   In-portal ePUB reader using epub.js.
   No file download path — content loaded from base64
   via Cloud Function. Progress, bookmarks, and
   highlights synced to Firestore.
═══════════════════════════════════════════════════ */

const getEbookContent = httpsCallable(functions, 'getEbookContent', { timeout: 120000 });
const sendToKindleFn = httpsCallable(functions, 'sendToKindle', { timeout: 120000 });
const sendToKoboFn = httpsCallable(functions, 'sendToKobo', { timeout: 120000 });

const FONT_SIZES = [
  { label: 'Small', value: 90 },
  { label: 'Medium', value: 100 },
  { label: 'Large', value: 120 },
  { label: 'X-Large', value: 140 },
];

const THEMES = {
  light: { bg: '#ffffff', fg: '#1e293b', name: 'Light', icon: Sun },
  sepia: { bg: '#f5f0e8', fg: '#5c4b37', name: 'Sepia', icon: Palette },
  dark: { bg: '#1e293b', fg: '#e2e8f0', name: 'Dark', icon: Moon },
};

export default function EbookReader({ ebook, onBack }) {
  const user = auth.currentUser;
  const ebookId = ebook?.id;
  const authorId = ebook?.authorId;

  // Reader state
  const [isLoading, setIsLoading] = useState(true);
  const [renditionReady, setRenditionReady] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentChapterTitle, setCurrentChapterTitle] = useState('');

  // Panels
  const [tocOpen, setTocOpen] = useState(false);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Settings
  const [fontSize, setFontSize] = useState(100);
  const [theme, setTheme] = useState('light');

  // Table of contents
  const [toc, setToc] = useState([]);

  // Bookmarks and highlights
  const [bookmarks, setBookmarks] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [showHighlightMenu, setShowHighlightMenu] = useState(false);
  const [selectedCfiRange, setSelectedCfiRange] = useState(null);
  const [selectedText, setSelectedText] = useState('');
  const [editingHighlight, setEditingHighlight] = useState(null);
  const [highlightNote, setHighlightNote] = useState('');
  const [highlightMenuPos, setHighlightMenuPos] = useState({ x: 0, y: 0 });

  // ─── Offline state (Capacitor native only) ───
  const [isNativeApp] = useState(() => isNative());
  const [offlineToast, setOfflineToast] = useState(null); // 'Saved for offline reading' toast

  // Send to Device. Opens automatically when arriving from the "Send to device"
  // button in My Library, which navigates to /read/{id}#send — same panel and
  // same sendToKindle/sendToKobo flow as the in-reader button.
  const [devicePanelOpen, setDevicePanelOpen] = useState(
    () => typeof window !== 'undefined' && window.location.hash === '#send'
  );
  const [deviceEmails, setDeviceEmails] = useState({ kindleEmail: '', koboEmail: '' });
  const [deviceSending, setDeviceSending] = useState(null); // 'kindle' | 'kobo' | null
  const [deviceHelpOpen, setDeviceHelpOpen] = useState(false);
  const [deviceToast, setDeviceToast] = useState(null); // { type, message }
  const [confirmSendMethod, setConfirmSendMethod] = useState(null); // 'kindle' | 'kobo' | null
  const [kindleDelivery, setKindleDelivery] = useState(null); // last delivery doc
  const [koboDelivery, setKoboDelivery] = useState(null); // last delivery doc

  // Refs
  const viewerRef = useRef(null);
  const bookRef = useRef(null);
  const renditionRef = useRef(null);
  const touchStartX = useRef(null);
  const progressSaveTimeout = useRef(null);

  // ─── Load ebook content ───
  useEffect(() => {
    if (!user || !ebookId) return;
    loadEbook();
    loadBookmarksAndHighlights();
    return () => {
      setRenditionReady(false);
      if (bookRef.current) {
        bookRef.current.destroy();
        bookRef.current = null;
      }
      renditionRef.current = null;
      if (progressSaveTimeout.current) {
        clearTimeout(progressSaveTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, ebookId]);

  // ─── Native only: sync pending ebook progress when coming back online ───
  useEffect(() => {
    if (!isNativeApp || !user) return;
    const unsubscribe = onNetworkChange(async (connected) => {
      if (connected) {
        await syncPendingEbookProgress();
      }
    });
    syncPendingEbookProgress();
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNativeApp, user]);

  async function syncPendingEbookProgress() {
    try {
      const pending = await getPendingProgress();
      const ebookEntries = pending.filter(e => e.type === 'ebook');
      if (ebookEntries.length === 0) return;
      const online = await isOnline();
      if (!online) return;
      for (const entry of ebookEntries) {
        await setDoc(doc(db, 'ebookProgress', entry.docId), {
          uid: entry.uid,
          ebookId: entry.ebookId,
          cfi: entry.cfi,
          percentage: entry.percentage,
          fontSize: entry.fontSize,
          theme: entry.theme,
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }
      // Clear only ebook entries — leave audiobook entries for audiobook player
      const remaining = pending.filter(e => e.type !== 'ebook');
      if (remaining.length === 0) {
        await clearPendingProgress();
      }
      console.log('[EbookReader] Synced offline progress');
    } catch (err) {
      console.warn('[EbookReader] Failed to sync offline progress:', err);
    }
  }

  // ─── Load device emails and delivery status ───
  useEffect(() => {
    if (!user || !ebookId) return;
    // Load device emails from readerProfiles/{uid}
    getDoc(doc(db, 'readerProfiles', user.uid)).then(snap => {
      if (snap.exists()) {
        const d = snap.data();
        setDeviceEmails({ kindleEmail: d.kindleEmail || '', koboEmail: d.koboEmail || '' });
      }
    }).catch(() => {});
    // Load Kindle delivery status
    getDoc(doc(db, 'ebookDeliveries', `${user.uid}_${ebookId}`)).then(snap => {
      if (snap.exists()) setKindleDelivery(snap.data());
    }).catch(() => {});
    // Load Kobo delivery status
    getDoc(doc(db, 'ebookDeliveries', `${user.uid}_${ebookId}_kobo`)).then(snap => {
      if (snap.exists()) setKoboDelivery(snap.data());
    }).catch(() => {});
  }, [user, ebookId]);

  // ─── Strip the #send marker once consumed, so a refresh doesn't force the panel ───
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#send') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  // ─── Apply theme and font size changes ───
  useEffect(() => {
    if (!renditionRef.current) return;
    const t = THEMES[theme];
    renditionRef.current.themes.override('color', t.fg);
    renditionRef.current.themes.override('background-color', t.bg);
  }, [theme]);

  useEffect(() => {
    if (!renditionRef.current) return;
    renditionRef.current.themes.fontSize(`${fontSize}%`);
  }, [fontSize]);

  // ─── Keyboard navigation ───
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        renditionRef.current?.next();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        renditionRef.current?.prev();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ─── Resize handler — adjust rendition width ───
  useEffect(() => {
    function handleResize() {
      if (!renditionRef.current || !viewerRef.current) return;
      const containerWidth = viewerRef.current.clientWidth;
      const readerWidth = Math.min(containerWidth, 720);
      renditionRef.current.resize(readerWidth, '100%');
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  async function loadEbook() {
    try {
      setIsLoading(true);
      setError(null);

      // Native only: check for cached ePUB content first
      let base64 = null;
      if (isNativeApp) {
        const cached = await getCachedEbookContent(user.uid, ebookId);
        if (cached) {
          base64 = cached;
          console.log('[EbookReader] Loaded from offline cache');
        }
      }

      // Fall back to Cloud Function if no cache
      if (!base64) {
        console.log('[EbookReader] Fetching content from Cloud Function...');
        const result = await getEbookContent({ ebookId });
        if (!result?.data) throw new Error('Cloud Function returned no data');
        base64 = result.data.content;
        if (!base64 || typeof base64 !== 'string' || base64.length === 0) {
          throw new Error('Cloud Function returned empty or invalid ePUB content');
        }

        // Native only: cache ePUB for offline reading
        if (isNativeApp) {
          cacheEbookContent(user.uid, ebookId, base64).then(isFirstCache => {
            if (isFirstCache) {
              setOfflineToast('Saved for offline reading');
              setTimeout(() => setOfflineToast(null), 3000);
            }
          }).catch(err => {
            console.warn('[EbookReader] Failed to cache ePUB:', err);
          });
        }
      }

      console.log(`[EbookReader] Content ${base64.length} chars, decoding...`);

      // Convert base64 to ArrayBuffer
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const arrayBuffer = bytes.buffer;

      console.log(`[EbookReader] Decoded ${arrayBuffer.byteLength} bytes, creating ePub...`);

      // Create epub.js book
      const book = ePub(arrayBuffer);
      bookRef.current = book;

      // Wait for book to be ready
      await book.ready;
      console.log('[EbookReader] Book ready');

      // Get table of contents
      const navigation = await book.loaded.navigation;
      const tocItems = navigation.toc || [];
      setToc(tocItems);
      console.log(`[EbookReader] TOC loaded: ${tocItems.length} items`);

      if (!viewerRef.current) {
        throw new Error('Viewer container not available');
      }

      // Calculate reader width — max 720px, with padding
      const containerWidth = viewerRef.current.clientWidth;
      const readerWidth = Math.min(containerWidth, 720);

      // Render into the viewer container (always in DOM, behind loading overlay)
      const rendition = book.renderTo(viewerRef.current, {
        width: readerWidth,
        height: '100%',
        spread: 'none',
        flow: 'paginated',
      });
      renditionRef.current = rendition;

      // Apply initial theme with readable typography
      const t = THEMES[theme];
      rendition.themes.default({
        'body': {
          'color': t.fg + ' !important',
          'background-color': t.bg + ' !important',
          'user-select': 'text',
          '-webkit-user-select': 'text',
          'padding': '0 24px !important',
          'line-height': '1.6 !important',
          'font-family': 'Georgia, "Times New Roman", serif !important',
          'max-width': '720px !important',
          'margin': '0 auto !important',
          'box-sizing': 'border-box !important',
        },
        'p, span, div, li, a, em, strong, blockquote, td, th': {
          'color': 'inherit !important',
          'line-height': 'inherit !important',
        },
        'h1, h2, h3, h4, h5, h6': {
          'color': 'inherit !important',
          'line-height': '1.3 !important',
        },
      });
      rendition.themes.fontSize(`${fontSize}%`);

      // Disable context menu on reader content
      rendition.on('rendered', () => {
        const iframe = viewerRef.current?.querySelector('iframe');
        if (iframe?.contentDocument) {
          iframe.contentDocument.addEventListener('contextmenu', (e) => {
            e.preventDefault();
          });
        }
      });

      // Text selection for highlights
      rendition.on('selected', (cfiRange) => {
        const text = rendition.getRange(cfiRange)?.toString() || '';
        if (text.trim().length > 0) {
          setSelectedCfiRange(cfiRange);
          setSelectedText(text.trim());
          // Position menu near the top of the viewer
          const viewerRect = viewerRef.current?.getBoundingClientRect();
          if (viewerRect) {
            setHighlightMenuPos({ x: viewerRect.width / 2, y: 60 });
          }
          setShowHighlightMenu(true);
        }
      });

      // Track location changes for progress
      rendition.on('relocated', (location) => {
        if (location?.start?.cfi) {
          debouncedSaveProgress(location.start.cfi);
        }
        // Update progress percentage — only works after locations are generated
        if (location?.start?.percentage !== undefined) {
          setProgress(Math.round(location.start.percentage * 100));
        }

        // Update current chapter title
        if (location?.start?.href) {
          const chapter = tocItems.find(t =>
            t.href && location.start.href.includes(t.href.split('#')[0])
          );
          if (chapter) {
            setCurrentChapterTitle(chapter.label?.trim() || '');
          }
        }
      });

      // Touch handling for swipe
      rendition.on('touchstart', (e) => {
        touchStartX.current = e.changedTouches?.[0]?.clientX || null;
      });
      rendition.on('touchend', (e) => {
        if (touchStartX.current === null) return;
        const endX = e.changedTouches?.[0]?.clientX;
        if (endX === undefined) return;
        const diff = endX - touchStartX.current;
        if (Math.abs(diff) > 50) {
          if (diff > 0) rendition.prev();
          else rendition.next();
        }
        touchStartX.current = null;
      });

      // Load saved progress and display
      const progressDocId = `${user.uid}_${ebookId}`;
      const progressSnap = await getDoc(doc(db, 'ebookProgress', progressDocId));
      if (progressSnap.exists()) {
        const savedCfi = progressSnap.data().cfi;
        if (savedCfi) {
          await rendition.display(savedCfi);
        } else {
          await rendition.display();
        }
        // Restore saved settings
        const savedData = progressSnap.data();
        if (savedData.fontSize) setFontSize(savedData.fontSize);
        if (savedData.theme) setTheme(savedData.theme);
      } else {
        await rendition.display();
      }
      console.log('[EbookReader] Displayed successfully');
      setRenditionReady(true);
      setIsLoading(false);

      // Re-apply highlights from Firestore
      loadHighlightsIntoRendition(rendition);

      // Generate locations in the background — this is slow for large books
      // Progress percentage won't work until this completes, but the reader is usable
      book.locations.generate(1024).then(() => {
        console.log('[EbookReader] Locations generated');
      }).catch(err => {
        console.warn('[EbookReader] Locations generation failed (non-critical):', err);
      });

    } catch (err) {
      console.error('[EbookReader] Failed to load eBook:', err);
      setError(`Failed to load eBook: ${err.message}`);
      setIsLoading(false);
    }
  }

  async function loadBookmarksAndHighlights() {
    try {
      // Load bookmarks
      const bmQuery = query(
        collection(db, 'ebookBookmarks'),
        where('uid', '==', user.uid),
        where('ebookId', '==', ebookId),
        where('type', '==', 'bookmark'),
        orderBy('createdAt', 'desc')
      );
      const bmSnap = await getDocs(bmQuery);
      setBookmarks(bmSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load highlights
      const hlQuery = query(
        collection(db, 'ebookBookmarks'),
        where('uid', '==', user.uid),
        where('ebookId', '==', ebookId),
        where('type', '==', 'highlight'),
        orderBy('createdAt', 'desc')
      );
      const hlSnap = await getDocs(hlQuery);
      setHighlights(hlSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Failed to load bookmarks/highlights:', err);
      setDeviceToast({ type: 'error', message: 'Could not load bookmarks. Please try again.' });
      setTimeout(() => setDeviceToast(null), 8000);
    }
  }

  function loadHighlightsIntoRendition(rendition) {
    // This will be called after highlights are loaded
    // Highlights are applied via rendition annotations
  }

  // Apply highlights whenever they change
  useEffect(() => {
    if (!renditionRef.current || highlights.length === 0) return;
    highlights.forEach(hl => {
      try {
        renditionRef.current.annotations.highlight(
          hl.cfiRange,
          {},
          () => {
            setEditingHighlight(hl);
            setHighlightNote(hl.note || '');
          },
          'hl',
          { fill: 'rgba(20, 184, 166, 0.3)', 'fill-opacity': '0.3' }
        );
      } catch {
        // CFI may not be valid in current section
      }
    });
  }, [highlights]);

  // ─── Progress saving (debounced) ───
  function debouncedSaveProgress(cfi) {
    if (progressSaveTimeout.current) {
      clearTimeout(progressSaveTimeout.current);
    }
    progressSaveTimeout.current = setTimeout(() => {
      saveProgress(cfi);
    }, 2000);
  }

  async function saveProgress(cfi) {
    try {
      const progressDocId = `${user.uid}_${ebookId}`;

      // Native only: if offline, save locally for later sync
      if (isNativeApp) {
        const online = await isOnline();
        if (!online) {
          await saveProgressOffline({
            type: 'ebook',
            docId: progressDocId,
            uid: user.uid,
            ebookId,
            cfi,
            percentage: progress,
            fontSize,
            theme,
          });
          return;
        }
      }

      await setDoc(doc(db, 'ebookProgress', progressDocId), {
        uid: user.uid,
        ebookId,
        cfi,
        percentage: progress,
        fontSize,
        theme,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch {
      // Non-critical — on native try offline save
      if (isNativeApp) {
        saveProgressOffline({
          type: 'ebook',
          docId: `${user.uid}_${ebookId}`,
          uid: user.uid,
          ebookId,
          cfi,
          percentage: progress,
          fontSize,
          theme,
        }).catch(() => {});
      }
    }
  }

  // ─── Navigation ───
  function goToChapter(href) {
    if (!renditionRef.current) return;
    renditionRef.current.display(href);
    setTocOpen(false);
  }

  function handleTapNavigation(e) {
    if (!viewerRef.current || !renditionRef.current) return;

    // Don't navigate if the highlight menu is open
    if (showHighlightMenu) return;

    const rect = viewerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const third = rect.width / 3;

    if (x < third) {
      renditionRef.current.prev();
    } else if (x > third * 2) {
      renditionRef.current.next();
    }
  }

  // ─── Bookmarks ───
  async function toggleBookmark() {
    if (!renditionRef.current || !renditionReady) return;

    let cfi;
    try {
      const location = renditionRef.current.currentLocation();
      cfi = location?.start?.cfi;
    } catch {
      return;
    }
    if (!cfi) return;

    // Check if already bookmarked
    const existing = bookmarks.find(b => b.cfi === cfi);
    if (existing) {
      await deleteDoc(doc(db, 'ebookBookmarks', existing.id));
      setBookmarks(prev => prev.filter(b => b.id !== existing.id));
    } else {
      const data = {
        uid: user.uid,
        ebookId,
        type: 'bookmark',
        cfi,
        chapterTitle: currentChapterTitle || 'Unknown chapter',
        percentage: progress,
        createdAt: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, 'ebookBookmarks'), data);
      setBookmarks(prev => [{ id: docRef.id, ...data }, ...prev]);
    }
  }

  function jumpToBookmark(bm) {
    if (!renditionRef.current) return;
    renditionRef.current.display(bm.cfi || bm.cfiRange);
    setBookmarksOpen(false);
  }

  async function deleteBookmarkOrHighlight(item) {
    try {
      await deleteDoc(doc(db, 'ebookBookmarks', item.id));
      if (item.type === 'bookmark') {
        setBookmarks(prev => prev.filter(b => b.id !== item.id));
      } else {
        setHighlights(prev => prev.filter(h => h.id !== item.id));
        // Remove annotation from rendition
        try {
          renditionRef.current?.annotations.remove(item.cfiRange, 'highlight');
        } catch { /* skip */ }
      }
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  }

  // ─── Highlights ───
  async function createHighlight() {
    if (!selectedCfiRange || !selectedText) return;

    const data = {
      uid: user.uid,
      ebookId,
      type: 'highlight',
      cfiRange: selectedCfiRange,
      text: selectedText.slice(0, 500),
      note: '',
      chapterTitle: currentChapterTitle || 'Unknown chapter',
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'ebookBookmarks'), data);
    setHighlights(prev => [{ id: docRef.id, ...data }, ...prev]);

    // Apply annotation
    try {
      renditionRef.current?.annotations.highlight(
        selectedCfiRange,
        {},
        () => {
          setEditingHighlight({ id: docRef.id, ...data });
          setHighlightNote('');
        },
        'hl',
        { fill: 'rgba(20, 184, 166, 0.3)', 'fill-opacity': '0.3' }
      );
    } catch { /* skip */ }

    setShowHighlightMenu(false);
    setSelectedCfiRange(null);
    setSelectedText('');

    // Clear text selection in epub iframe
    const iframe = viewerRef.current?.querySelector('iframe');
    if (iframe?.contentWindow) {
      iframe.contentWindow.getSelection()?.removeAllRanges();
    }
  }

  async function saveHighlightNote() {
    if (!editingHighlight) return;
    try {
      await updateDoc(doc(db, 'ebookBookmarks', editingHighlight.id), {
        note: highlightNote.trim().slice(0, 500),
      });
      setHighlights(prev =>
        prev.map(h => h.id === editingHighlight.id ? { ...h, note: highlightNote.trim() } : h)
      );
      setEditingHighlight(null);
      setHighlightNote('');
    } catch (err) {
      console.error('Failed to save note:', err);
    }
  }

  // ─── Check if current page is bookmarked ───
  const isCurrentPageBookmarked = useCallback(() => {
    if (!renditionRef.current || !renditionReady) return false;
    try {
      const location = renditionRef.current.currentLocation();
      const cfi = location?.start?.cfi;
      return cfi && bookmarks.some(b => b.cfi === cfi);
    } catch {
      return false;
    }
  }, [bookmarks, renditionReady]);

  const themeConfig = THEMES[theme];

  // Check if a delivery was sent within the last 24 hours
  const isSentRecently = (delivery) => {
    if (!delivery?.lastSentAt) return false;
    const sentAt = delivery.lastSentAt.toDate ? delivery.lastSentAt.toDate() : new Date(delivery.lastSentAt);
    return (Date.now() - sentAt.getTime()) < 24 * 60 * 60 * 1000;
  };

  const formatSentTime = (delivery) => {
    if (!delivery?.lastSentAt) return '';
    const sentAt = delivery.lastSentAt.toDate ? delivery.lastSentAt.toDate() : new Date(delivery.lastSentAt);
    return sentAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const handleSendToDevice = async (method) => {
    setDeviceSending(method);
    setDeviceToast(null);
    try {
      const fn = method === 'kindle' ? sendToKindleFn : sendToKoboFn;
      await fn({ ebookId });
      // Refresh delivery status
      const docId = method === 'kindle' ? `${user.uid}_${ebookId}` : `${user.uid}_${ebookId}_kobo`;
      const snap = await getDoc(doc(db, 'ebookDeliveries', docId));
      if (snap.exists()) {
        if (method === 'kindle') setKindleDelivery(snap.data());
        else setKoboDelivery(snap.data());
      }
      const deviceName = method === 'kindle' ? 'Kindle' : 'Kobo';
      setDeviceToast({ type: 'success', message: `Sent to your ${deviceName} \u2014 it should appear in your library within a few minutes` });
      setTimeout(() => setDeviceToast(null), 8000);
    } catch (err) {
      const msg = err?.message || err?.details || 'Failed to send. Please try again.';
      setDeviceToast({ type: 'error', message: msg });
      setTimeout(() => setDeviceToast(null), 8000);
    } finally {
      setDeviceSending(null);
    }
  };

  const hasAnyDeviceEmail = deviceEmails.kindleEmail || deviceEmails.koboEmail;

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{ backgroundColor: isLoading || error ? undefined : themeConfig.bg, color: isLoading || error ? undefined : themeConfig.fg }}
    >
      {/* ─── Loading overlay ─── */}
      {isLoading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-50">
          <div className="text-center">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-teal-600" />
            <p className="mt-4 text-sm text-slate-500">Loading eBook...</p>
            <p className="mt-1 text-xs text-slate-400">This may take a moment for larger files</p>
          </div>
        </div>
      )}

      {/* ─── Error overlay ─── */}
      {error && !isLoading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-50 p-4">
          <Card className="w-full max-w-md border-slate-200">
            <CardContent className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
                <BookOpen className="h-8 w-8 text-red-400" />
              </div>
              <h2 className="mb-2 text-lg font-semibold text-slate-800">Could not load eBook</h2>
              <p className="mb-6 text-sm text-slate-500">{error}</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={onBack} variant="outline" className="gap-1.5 border-slate-200">
                  <ArrowLeft className="h-4 w-4" /> Go back
                </Button>
                <Button onClick={loadEbook} className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white">
                  Try again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* ─── Header ─── */}
      <header
        className="sticky top-0 z-30 border-b px-4 py-2"
        style={{
          backgroundColor: theme === 'dark' ? '#0f172a' : '#ffffff',
          borderColor: theme === 'dark' ? '#334155' : '#e2e8f0',
        }}
      >
        <div className="mx-auto flex max-w-4xl items-center gap-2">
          <Button
            onClick={onBack}
            variant="ghost"
            size="sm"
            className={`gap-1 ${theme === 'dark' ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500'}`}
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>

          <div className="min-w-0 flex-1 text-center">
            <h1
              className="truncate text-sm font-semibold"
              style={{ color: themeConfig.fg }}
            >
              {ebook?.title || 'eBook'}
            </h1>
            {currentChapterTitle && (
              <p className="truncate text-xs opacity-60">{currentChapterTitle}</p>
            )}
          </div>

          <div className="flex items-center gap-0.5">
            <Button
              onClick={() => { setTocOpen(!tocOpen); setBookmarksOpen(false); setSettingsOpen(false); setDevicePanelOpen(false); }}
              variant="ghost" size="sm"
              className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}
              title="Contents"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              onClick={toggleBookmark}
              variant="ghost" size="sm"
              className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}
              title="Bookmark this page"
            >
              {isCurrentPageBookmarked()
                ? <BookmarkCheck className="h-4 w-4 text-teal-500" />
                : <Bookmark className="h-4 w-4" />
              }
            </Button>
            <Button
              onClick={() => { setBookmarksOpen(!bookmarksOpen); setTocOpen(false); setSettingsOpen(false); setDevicePanelOpen(false); }}
              variant="ghost" size="sm"
              className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}
              title="Bookmarks and highlights"
            >
              <Highlighter className="h-4 w-4" />
            </Button>
            <Button
              onClick={() => { setDevicePanelOpen(!devicePanelOpen); setSettingsOpen(false); setTocOpen(false); setBookmarksOpen(false); }}
              variant="ghost" size="sm"
              className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}
              title="Send to device"
            >
              <Smartphone className="h-4 w-4" />
            </Button>
            <Button
              onClick={() => { setSettingsOpen(!settingsOpen); setTocOpen(false); setBookmarksOpen(false); setDevicePanelOpen(false); }}
              variant="ghost" size="sm"
              className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}
              title="Reading settings"
            >
              <Type className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* ─── Main content ─── */}
      <div className="relative flex flex-1">
        {/* TOC sidebar */}
        {tocOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/30"
              onClick={() => setTocOpen(false)}
            />
            <aside
              className="fixed inset-y-0 left-0 z-50 w-80 overflow-y-auto border-r pt-14 shadow-lg"
              style={{
                backgroundColor: theme === 'dark' ? '#0f172a' : '#ffffff',
                borderColor: theme === 'dark' ? '#334155' : '#e2e8f0',
              }}
            >
              <div
                className="flex items-center justify-between border-b px-4 py-3"
                style={{ borderColor: theme === 'dark' ? '#334155' : '#f1f5f9' }}
              >
                <h2 className="text-sm font-semibold" style={{ color: themeConfig.fg }}>Contents</h2>
                <Button
                  onClick={() => setTocOpen(false)}
                  variant="ghost" size="sm"
                  className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div>
                {toc.map((item, i) => (
                  <div
                    key={i}
                    onClick={() => goToChapter(item.href)}
                    className="cursor-pointer px-4 py-3 text-sm transition-colors"
                    style={{
                      color: themeConfig.fg,
                      paddingLeft: item.subitems?.length ? '1rem' : '1rem',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#1e293b' : '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {item.label?.trim()}
                  </div>
                ))}
              </div>
            </aside>
          </>
        )}

        {/* Bookmarks and highlights panel */}
        {bookmarksOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/30"
              onClick={() => setBookmarksOpen(false)}
            />
            <aside
              className="fixed inset-y-0 right-0 z-50 w-80 overflow-y-auto border-l pt-14 shadow-lg"
              style={{
                backgroundColor: theme === 'dark' ? '#0f172a' : '#ffffff',
                borderColor: theme === 'dark' ? '#334155' : '#e2e8f0',
              }}
            >
              <div
                className="flex items-center justify-between border-b px-4 py-3"
                style={{ borderColor: theme === 'dark' ? '#334155' : '#f1f5f9' }}
              >
                <h2 className="text-sm font-semibold" style={{ color: themeConfig.fg }}>
                  Bookmarks &amp; Highlights
                </h2>
                <Button
                  onClick={() => setBookmarksOpen(false)}
                  variant="ghost" size="sm"
                  className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Bookmarks section */}
              <div className="px-4 pt-3 pb-1">
                <p className="text-xs font-semibold uppercase tracking-wide opacity-50">Bookmarks</p>
              </div>
              {bookmarks.length === 0 ? (
                <p className="px-4 py-2 text-sm opacity-50">No bookmarks yet</p>
              ) : (
                bookmarks.map(bm => (
                  <div
                    key={bm.id}
                    className="flex items-start gap-2 px-4 py-2 cursor-pointer transition-colors"
                    onClick={() => jumpToBookmark(bm)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#1e293b' : '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Bookmark className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-teal-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium">{bm.chapterTitle}</p>
                      <p className="text-xs opacity-50">{bm.percentage || 0}% through</p>
                    </div>
                    <Button
                      onClick={(e) => { e.stopPropagation(); deleteBookmarkOrHighlight(bm); }}
                      variant="ghost" size="sm"
                      className="opacity-30 hover:opacity-100 hover:text-red-500"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))
              )}

              {/* Highlights section */}
              <div className="px-4 pt-4 pb-1">
                <p className="text-xs font-semibold uppercase tracking-wide opacity-50">Highlights</p>
              </div>
              {highlights.length === 0 ? (
                <p className="px-4 py-2 text-sm opacity-50">No highlights yet</p>
              ) : (
                highlights.map(hl => (
                  <div
                    key={hl.id}
                    className="px-4 py-2 cursor-pointer transition-colors"
                    onClick={() => jumpToBookmark(hl)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#1e293b' : '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <div className="flex items-start gap-2">
                      <Highlighter className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-teal-500" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs italic opacity-70 line-clamp-2">
                          &ldquo;{hl.text}&rdquo;
                        </p>
                        <p className="text-xs opacity-50 mt-0.5">{hl.chapterTitle}</p>
                        {hl.note && (
                          <p className="text-xs mt-1 flex items-start gap-1">
                            <StickyNote className="h-3 w-3 flex-shrink-0 mt-0.5 text-teal-400" />
                            {hl.note}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingHighlight(hl);
                            setHighlightNote(hl.note || '');
                          }}
                          variant="ghost" size="sm"
                          className="opacity-30 hover:opacity-100"
                          title="Edit note"
                        >
                          <StickyNote className="h-3 w-3" />
                        </Button>
                        <Button
                          onClick={(e) => { e.stopPropagation(); deleteBookmarkOrHighlight(hl); }}
                          variant="ghost" size="sm"
                          className="opacity-30 hover:opacity-100 hover:text-red-500"
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </aside>
          </>
        )}

        {/* Settings panel */}
        {settingsOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/30"
              onClick={() => setSettingsOpen(false)}
            />
            <div
              className="fixed right-4 top-14 z-50 w-64 rounded-lg border p-4 shadow-lg"
              style={{
                backgroundColor: theme === 'dark' ? '#0f172a' : '#ffffff',
                borderColor: theme === 'dark' ? '#334155' : '#e2e8f0',
              }}
            >
              <h3 className="text-sm font-semibold mb-3" style={{ color: themeConfig.fg }}>
                Reading settings
              </h3>

              {/* Font size */}
              <p className="text-xs opacity-50 mb-2">Font size</p>
              <div className="grid grid-cols-4 gap-1 mb-4">
                {FONT_SIZES.map(fs => (
                  <Button
                    key={fs.value}
                    onClick={() => setFontSize(fs.value)}
                    variant={fontSize === fs.value ? 'default' : 'outline'}
                    size="sm"
                    className={`text-xs ${
                      fontSize === fs.value
                        ? 'bg-teal-600 text-white hover:bg-teal-700'
                        : theme === 'dark'
                          ? 'border-slate-600 text-slate-300'
                          : ''
                    }`}
                  >
                    {fs.label}
                  </Button>
                ))}
              </div>

              {/* Theme */}
              <p className="text-xs opacity-50 mb-2">Theme</p>
              <div className="grid grid-cols-3 gap-1">
                {Object.entries(THEMES).map(([key, t]) => {
                  const Icon = t.icon;
                  return (
                    <Button
                      key={key}
                      onClick={() => setTheme(key)}
                      variant={theme === key ? 'default' : 'outline'}
                      size="sm"
                      className={`gap-1 text-xs ${
                        theme === key
                          ? 'bg-teal-600 text-white hover:bg-teal-700'
                          : theme === 'dark'
                            ? 'border-slate-600 text-slate-300'
                            : ''
                      }`}
                    >
                      <Icon className="h-3 w-3" />
                      {t.name}
                    </Button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Send to Device panel */}
        {devicePanelOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setDevicePanelOpen(false)} />
            <div
              className="fixed right-4 top-14 z-50 w-72 rounded-lg border p-4 shadow-lg"
              style={{
                backgroundColor: theme === 'dark' ? '#0f172a' : '#ffffff',
                borderColor: theme === 'dark' ? '#334155' : '#e2e8f0',
              }}
            >
              <h3 className="text-sm font-semibold mb-3" style={{ color: themeConfig.fg }}>
                Send to device
              </h3>

              {!hasAnyDeviceEmail ? (
                <div className="space-y-2">
                  <p className="text-xs" style={{ color: themeConfig.fg, opacity: 0.7 }}>
                    Add your Kindle or Kobo email in your profile to send this book to your device
                  </p>
                  <Button
                    onClick={() => {
                      setDevicePanelOpen(false);
                      window.location.href = '/reader#profile';
                    }}
                    size="sm"
                    className="w-full bg-teal-600 text-white hover:bg-teal-700 text-xs"
                  >
                    Go to profile settings
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Collapsible setup instructions */}
                  <div>
                    <button
                      onClick={() => setDeviceHelpOpen(!deviceHelpOpen)}
                      className="flex w-full items-center gap-1.5 text-xs font-medium transition-colors"
                      style={{ color: themeConfig.fg, opacity: 0.6 }}
                    >
                      <HelpCircle className="h-3.5 w-3.5 flex-shrink-0" />
                      How does this work?
                      {deviceHelpOpen
                        ? <ChevronUp className="ml-auto h-3.5 w-3.5" />
                        : <ChevronDown className="ml-auto h-3.5 w-3.5" />
                      }
                    </button>
                    {deviceHelpOpen && (
                      <div className="mt-2 space-y-3 rounded-md border px-3 py-2.5 text-xs leading-relaxed"
                        style={{
                          borderColor: theme === 'dark' ? '#334155' : '#e2e8f0',
                          color: themeConfig.fg,
                          opacity: 0.8,
                        }}
                      >
                        {deviceEmails.kindleEmail && (
                          <div>
                            <p className="font-semibold mb-1">Kindle</p>
                            <p className="mb-1">Before sending, make sure <strong>arc@narratoria.com</strong> is on your approved senders list:</p>
                            <ol className="list-decimal ml-4 space-y-0.5">
                              <li>Go to amazon.com and sign in</li>
                              <li>Click Account and Lists and go to Manage Your Content and Devices</li>
                              <li>Go to Preferences, then Personal Document Settings</li>
                              <li>Under Approved Personal Document Email List, click Add a new approved email address</li>
                              <li>Enter <strong>arc@narratoria.com</strong> and save</li>
                              <li>Your book will arrive in your Kindle library within a few minutes of sending</li>
                            </ol>
                          </div>
                        )}
                        {deviceEmails.koboEmail && (
                          <div>
                            <p className="font-semibold mb-1">Kobo</p>
                            <p>Your Kobo email is the address registered to your Kobo account. The book will be sent directly to your Kobo library.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Send buttons */}
                  {deviceEmails.kindleEmail && (
                    <div>
                      {isSentRecently(kindleDelivery) ? (
                        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2">
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-green-800">Sent to Kindle today</p>
                            <p className="text-xs text-green-600">at {formatSentTime(kindleDelivery)}</p>
                          </div>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setConfirmSendMethod('kindle')}
                          disabled={deviceSending !== null}
                          size="sm"
                          className="w-full bg-teal-600 text-white hover:bg-teal-700 text-xs gap-1"
                        >
                          Send to Kindle
                        </Button>
                      )}
                    </div>
                  )}
                  {deviceEmails.koboEmail && (
                    <div>
                      {isSentRecently(koboDelivery) ? (
                        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2">
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-green-800">Sent to Kobo today</p>
                            <p className="text-xs text-green-600">at {formatSentTime(koboDelivery)}</p>
                          </div>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setConfirmSendMethod('kobo')}
                          disabled={deviceSending !== null}
                          size="sm"
                          className="w-full bg-teal-600 text-white hover:bg-teal-700 text-xs gap-1"
                        >
                          Send to Kobo
                        </Button>
                      )}
                    </div>
                  )}
                  <p className="text-xs opacity-50 mt-1" style={{ color: themeConfig.fg }}>
                    One send per device per 24 hours
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Send to device confirmation modal */}
        <Dialog open={confirmSendMethod !== null} onOpenChange={() => setConfirmSendMethod(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {confirmSendMethod === 'kindle' ? 'Ready to send to your Kindle?' : 'Ready to send to your Kobo?'}
              </DialogTitle>
              <div className="space-y-3 pt-2 text-sm text-slate-600">
                {confirmSendMethod === 'kindle' ? (
                  <>
                    <p>
                      Before sending, make sure <strong>arc@narratoria.com</strong> is on your
                      Kindle approved senders list, otherwise the book will not arrive.
                    </p>
                    <div>
                      <p className="font-medium text-slate-700">To add it:</p>
                      <ol className="mt-1 ml-4 list-decimal space-y-0.5">
                        <li>Go to amazon.com and sign in</li>
                        <li>Go to Account and Lists, then Manage Your Content and Devices</li>
                        <li>Click Preferences, then Personal Document Settings</li>
                        <li>Under Approved Personal Document Email List, add <strong>arc@narratoria.com</strong></li>
                      </ol>
                    </div>
                    <p>
                      Your book will be sent to: <strong>{deviceEmails.kindleEmail}</strong>
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      Your book will be sent as an ePUB file to: <strong>{deviceEmails.koboEmail}</strong>
                    </p>
                    <p>It will arrive in your Kobo library automatically.</p>
                  </>
                )}
              </div>
            </DialogHeader>
            <div className="flex gap-3 pt-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setConfirmSendMethod(null)}
                className="border-slate-200"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const method = confirmSendMethod;
                  setConfirmSendMethod(null);
                  handleSendToDevice(method);
                }}
                disabled={deviceSending !== null}
                className="bg-teal-600 text-white hover:bg-teal-700 gap-1"
              >
                {deviceSending ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
                ) : confirmSendMethod === 'kindle' ? (
                  'I have added it, send now'
                ) : (
                  'Send now'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Toast notification */}
        {deviceToast && (
          <div className={`fixed bottom-20 left-1/2 z-50 -translate-x-1/2 max-w-sm rounded-lg px-4 py-3 shadow-lg text-sm ${
            deviceToast.type === 'success'
              ? 'border border-green-200 bg-green-50 text-green-800'
              : 'border border-red-200 bg-red-50 text-red-800'
          }`}>
            {deviceToast.message}
          </div>
        )}

        {/* Native only: offline cache toast */}
        {offlineToast && (
          <div className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2 max-w-sm rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 shadow-lg text-sm text-teal-800 flex items-center gap-2">
            <Wifi className="h-4 w-4 flex-shrink-0" />
            {offlineToast}
          </div>
        )}

        {/* Highlight menu (floating) */}
        {showHighlightMenu && (
          <div
            className="fixed z-50 flex items-center gap-1 rounded-lg border bg-white p-2 shadow-lg"
            style={{
              left: `${highlightMenuPos.x}px`,
              top: `${highlightMenuPos.y}px`,
              transform: 'translateX(-50%)',
            }}
          >
            <Button
              onClick={createHighlight}
              size="sm"
              className="gap-1 bg-teal-600 text-white hover:bg-teal-700 text-xs"
            >
              <Highlighter className="h-3 w-3" /> Highlight
            </Button>
            <Button
              onClick={() => { setShowHighlightMenu(false); setSelectedCfiRange(null); setSelectedText(''); }}
              variant="ghost" size="sm"
              className="text-slate-500"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}

        {/* Note editor modal */}
        {editingHighlight && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <Card className="w-full max-w-sm border-slate-200">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-sm font-semibold text-slate-800">Edit note</h3>
                {editingHighlight.text && (
                  <p className="text-xs italic text-slate-500 line-clamp-2">
                    &ldquo;{editingHighlight.text}&rdquo;
                  </p>
                )}
                <Textarea
                  value={highlightNote}
                  onChange={(e) => setHighlightNote(e.target.value.slice(0, 500))}
                  placeholder="Add a note to this highlight..."
                  className="text-sm"
                  rows={3}
                />
                <p className="text-right text-xs text-slate-400">{highlightNote.length}/500</p>
                <div className="flex gap-2 justify-end">
                  <Button
                    onClick={() => { setEditingHighlight(null); setHighlightNote(''); }}
                    variant="outline" size="sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveHighlightNote}
                    size="sm"
                    className="bg-teal-600 text-white hover:bg-teal-700"
                  >
                    Save note
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ePUB viewer */}
        <div
          className="flex-1 relative flex justify-center"
          onClick={handleTapNavigation}
          style={{ userSelect: 'none' }}
          onContextMenu={(e) => e.preventDefault()}
        >
          {/* Tap zones - visual indicators on hover */}
          <div className="absolute inset-y-0 left-0 w-1/3 z-10 cursor-pointer flex items-center justify-start pl-2 opacity-0 hover:opacity-100 transition-opacity">
            <ChevronLeft className="h-8 w-8 opacity-20" style={{ color: themeConfig.fg }} />
          </div>
          <div className="absolute inset-y-0 right-0 w-1/3 z-10 cursor-pointer flex items-center justify-end pr-2 opacity-0 hover:opacity-100 transition-opacity">
            <ChevronRight className="h-8 w-8 opacity-20" style={{ color: themeConfig.fg }} />
          </div>

          <div
            ref={viewerRef}
            className="h-full"
            style={{
              width: '100%',
              maxWidth: '720px',
              minHeight: 'calc(100vh - 7rem)',
              backgroundColor: themeConfig.bg,
              padding: '0 24px',
            }}
          />
        </div>
      </div>

      {/* ─── Progress bar (bottom) ─── */}
      <div
        className="sticky bottom-0 z-30 border-t px-4 py-2"
        style={{
          backgroundColor: theme === 'dark' ? '#0f172a' : '#ffffff',
          borderColor: theme === 'dark' ? '#334155' : '#e2e8f0',
        }}
      >
        <div className="mx-auto max-w-4xl">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden"
              style={{ backgroundColor: theme === 'dark' ? '#334155' : '#e2e8f0' }}
            >
              <div
                className="h-full rounded-full bg-teal-600 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-xs whitespace-nowrap opacity-60" style={{ color: themeConfig.fg }}>
              {progress}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
